import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Boxes, Plus, Search, Filter, Rocket } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import {
  Button,
  Input,
  Skeleton,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@vnetwork/ui/primitives';
import {
  DataTable,
  EmptyState,
  FilterBuilder,
  type FilterField,
  type FilterCondition,
} from '@vnetwork/ui/data';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useBuckets,
  useDeleteBucket,
  useEmptyBucket,
  s3ExtractError,
  type Bucket,
} from '@/api/s3';
import { S3ConsoleBreadcrumbs } from '../S3ConsoleBreadcrumbs';
import { bucketColumns } from './bucket-columns';
import { BucketRenameDialog } from './BucketRenameDialog';

type PendingAction = { kind: 'empty' | 'delete'; bucket: Bucket } | null;

// A bucket carries only a name + creation date, so "Created" is the one
// meaningful filter dimension (name is already covered by search).
const filterFields: FilterField[] = [{ value: 'created', label: 'Created', input: 'date' }];

/** Bucket list — create, browse, settings, empty and delete. */
export function BucketList({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const base = `/services/oss/${serviceId}`;

  const { data: buckets = [], isLoading, isError, refetch } = useBuckets(serviceId);
  const deleteBucket = useDeleteBucket(serviceId);
  const emptyBucket = useEmptyBucket(serviceId);

  const [search, setSearch] = useState('');
  const [conditions, setConditions] = useState<FilterCondition[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [pending, setPending] = useState<PendingAction>(null);
  const [renaming, setRenaming] = useState<string | null>(null);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Created-date bounds from the date condition (after | before | between).
    const cond = conditions.find((c) => c.field === 'created' && c.value.trim());
    let from = 0;
    let to = Number.POSITIVE_INFINITY;
    if (cond) {
      const [a = '', b = ''] = cond.value.split(',');
      const at = a ? Date.parse(a) : NaN;
      const bt = b ? Date.parse(b) : NaN;
      if (cond.operator === 'after' && !Number.isNaN(at)) from = at;
      else if (cond.operator === 'before' && !Number.isNaN(at)) to = at;
      else if (cond.operator === 'between') {
        if (!Number.isNaN(at)) from = at;
        if (!Number.isNaN(bt)) to = bt;
      }
    }
    return buckets.filter((bkt) => {
      if (q && !bkt.name.toLowerCase().includes(q)) return false;
      if (cond) {
        const t = bkt.creation_date ? Date.parse(bkt.creation_date) : NaN;
        if (Number.isNaN(t) || t < from || t > to) return false;
      }
      return true;
    });
  }, [buckets, search, conditions]);

  const columns = bucketColumns({
    onOpen: (b) => navigate(`${base}/buckets/${b.name}/objects`),
    onSettings: (b) => navigate(`${base}/buckets/${b.name}`),
    onRename: (b) => setRenaming(b.name),
    onEmpty: (b) => setPending({ kind: 'empty', bucket: b }),
    onDelete: (b) => setPending({ kind: 'delete', bucket: b }),
  });

  const runPending = () => {
    if (!pending) return;
    const { kind, bucket } = pending;
    const mutation = kind === 'delete' ? deleteBucket : emptyBucket;
    mutation.mutate(bucket.name, {
      onSuccess: () => {
        toast.success(kind === 'delete' ? 'Bucket deleted' : 'Bucket emptied');
        setPending(null);
      },
      onError: (err) => {
        toast.error(s3ExtractError(err, `Failed to ${kind} bucket`));
        setPending(null);
      },
    });
  };

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<S3ConsoleBreadcrumbs trail={[{ label: 'Buckets' }]} />}
        title={`Buckets${buckets.length ? ` (${buckets.length})` : ''}`}
        description="S3-compatible object buckets for this service."
        actions={
          <Button
            variant="primary"
            startIcon={<Plus size={15} />}
            onClick={() => navigate(`${base}/buckets/create`)}
          >
            Create Bucket
          </Button>
        }
      />

      {isError ? (
        <Alert
          tone="danger"
          title="Couldn't load buckets"
          actions={
            <Button
              size="sm"
              variant="secondary"
              onClick={() => refetch()}
            >
              Retry
            </Button>
          }
        >
          The storage service is temporarily unavailable.
        </Alert>
      ) : isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={44} />
          ))}
        </div>
      ) : (
        <>
          <Toolbar
            start={
              <Input
                placeholder="Search buckets…"
                startSlot={<Search size={15} />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ maxWidth: 320 }}
              />
            }
            end={
              <>
                <Popover open={filterOpen} onOpenChange={setFilterOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="secondary" startIcon={<Filter size={15} />}>
                      Filters{conditions.length ? ` (${conditions.length})` : ''}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" style={{ padding: 0 }}>
                    <FilterBuilder
                      fields={filterFields}
                      defaultValue={conditions.length ? conditions : undefined}
                      onApply={(c) => {
                        setConditions(c.filter((x) => x.value.trim()));
                        setFilterOpen(false);
                      }}
                      onClose={() => setFilterOpen(false)}
                    />
                  </PopoverContent>
                </Popover>
                {conditions.length > 0 && (
                  <Button variant="ghost" onClick={() => setConditions([])}>
                    Clear
                  </Button>
                )}
              </>
            }
          />

          <DataTable
            columns={columns}
            data={rows}
            getRowId={(r) => r.name}
            onRowClick={(r) => navigate(`${base}/buckets/${r.name}/objects`)}
            emptyState={
              buckets.length === 0 ? (
                // No buckets at all — empty state inside the table (keeps the
                // column headers + toolbar, matching the CDN domains list).
                <EmptyState
                  icon={<Boxes size={28} />}
                  title="No buckets yet"
                  description="Onboard your first bucket to start storing objects."
                  actions={
                    <Button
                      startIcon={<Rocket size={16} />}
                      onClick={() => navigate('/services/oss/onboarding')}
                    >
                      Onboarding Bucket Now
                    </Button>
                  }
                />
              ) : (
                'No buckets match your search or filters.'
              )
            }
            footer={buckets.length ? `Showing ${rows.length} of ${buckets.length}` : undefined}
          />
        </>
      )}

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(o) => !o && setPending(null)}
        title={pending?.kind === 'delete' ? 'Delete Bucket' : 'Empty Bucket'}
        description={
          pending?.kind === 'delete'
            ? `This permanently deletes "${pending?.bucket.name}". The bucket must be empty. This cannot be undone.`
            : `This permanently deletes every object in "${pending?.bucket.name}". This cannot be undone.`
        }
        confirmLabel={pending?.kind === 'delete' ? 'Delete' : 'Empty'}
        destructive
        loading={deleteBucket.isPending || emptyBucket.isPending}
        onConfirm={runPending}
        requireText={pending?.bucket.name}
      />

      <BucketRenameDialog
        serviceId={serviceId}
        bucket={renaming}
        onOpenChange={(o) => !o && setRenaming(null)}
      />
    </Container>
  );
}
